import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';
import { IReporteBase } from 'modelos/src';
import { IVehiculoEnvio } from '../../../modulos/entidades/eventos-genericos/tratar-evento-generico/enviar-movil/enviar-movil.component';

/**
 * VehiculosLayerManager
 *
 * Manager para renderizar vehículos (trackers 4G, Qualcomm, T1000B) en el mapa.
 *
 * Características:
 * - Sistema de DOS capas (background + foreground)
 * - Background: brújula rotable si tiene orientación, círculo fijo si no
 * - Foreground: icono de vehículo siempre fijo
 * - Soporta clustering
 * - Color de cluster: azul (#2195f3)
 *
 * Tipos de reportes soportados:
 * - Tracker 4G
 * - Tracker Qualcomm
 * - Tracker T1000B
 */
@Injectable({
  providedIn: 'root',
})
export class VehiculosLayerManager extends BaseEntityLayerManager<
  IReporteBase<'Tracker 4G'>
> {
  // ========== CONFIGURACIÓN ==========
  readonly entityType = 'vehiculos';
  readonly supportsClustering = true;
  readonly clusterColor = '#2195f3'; // Azul

  // Opciones del manager
  private mostrarNombresMarkers = false;
  private idVehiculoSeleccionado: string | null = null; // ID del vehículo cuyo nombre se debe mostrar

  // IDs de layers específicos
  private backgroundLayerId = 'vehiculos-orientation-layer';
  private foregroundLayerId = 'vehiculos-layer';
  private textLayerId = 'vehiculos-text-layer';
  private unclusteredBackgroundLayerId =
    'vehiculos-unclustered-background-layer';
  private unclusteredForegroundLayerId =
    'vehiculos-unclustered-foreground-layer';

  // ========== RENDERIZACIÓN ==========

  /**
   * Convierte un reporte de vehículo a un Feature de GeoJSON
   */
  override entityToFeature(
    vehiculo: IReporteBase<'Tracker 4G'>,
  ): GeoJSON.Feature<GeoJSON.Point> {
    // OPTIMIZACIÓN: Almacenar en mapa en vez de serializar
    this.storeEntity(vehiculo);

    const valores = vehiculo.valores;
    const geojson = valores?.geojson;

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: geojson?.coordinates || [0, 0],
      },
      properties: {
        id: vehiculo._id, //ID del reporte
        idActivo: vehiculo.activo?._id || vehiculo._id, //ID del activo/vehículo (usado para filtros de nombre)
        tipo: vehiculo.tipoReporte,
        identificacion: vehiculo.activo?.identificacion || 'S/N',
        orientation: valores?.orientacion || null,
        hasOrientation:
          valores?.orientacion !== undefined && valores?.orientacion !== null,
        // Metadata adicional para popups
        velocidad: valores?.velocidad,
        // Diferenciación visual: true = vehículo de técnico (pin compuesto), false = default (icono + fondo)
        esTecnico: (vehiculo as any)._esTecnico ?? false,
      },
    };
  }

  /**
   * Expresión para el icono foreground (siempre fijo)
   */
  override getIconExpression(): string {
    return 'vehiculo-icon';
  }

  /**
   * Expresión para el icono background (brújula si tiene orientación, círculo si no)
   */
  getBackgroundIconExpression(): mapboxgl.Expression {
    return ['case', ['get', 'hasOrientation'], 'compass-icon', 'circle-icon'];
  }

  /**
   * Expresión para la rotación del background
   */
  getIconRotationExpression(): mapboxgl.Expression {
    return [
      'case',
      ['get', 'hasOrientation'],
      ['get', 'orientation'],
      0, // Sin rotación si no tiene orientación
    ];
  }

  // ========== CREACIÓN DE LAYERS ==========

  /**
   * Crea todos los layers necesarios para vehículos
   *
   * Orden de layers (bottom to top):
   * 1. Clusters (si clustering habilitado)
   * 2. Background layer (brújula/círculo)
   * 3. Foreground layer (icono vehículo)
   * 4. Unclustered layer (cuando clustering está ON, para items fuera de clusters)
   */
  protected createLayers(): void {
    if (!this.map) return;

    // 1. LAYERS DE CLUSTERING (si soporta clustering)
    if (this.supportsClustering) {
      this.createStandardClusterLayers();

      // Layers de vehículos NO CLUSTERED (cuando clustering está ON)
      // Sistema de DOS capas para mantener consistencia visual:

      // 1a. Background unclustered (brújula/círculo)
      this.addLayer({
        id: this.unclusteredBackgroundLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']], // Solo features que NO son clusters
        layout: {
          'icon-image': this.getBackgroundIconExpression(),
          'icon-size': 0.05, // 800 * 0.05 = 40px (compass.svg/circle.png son 800x800)
          'icon-allow-overlap': true,
          'icon-padding': 0,
          'icon-rotation-alignment': 'map', // IMPORTANTE: 'map' para que la brújula apunte al heading correcto
          'icon-rotate': this.getIconRotationExpression(),
          'icon-pitch-alignment': 'viewport', // 'viewport' para que no se pegue al mapa inclinado
          visibility: 'none', // Inicialmente oculto (se muestra cuando clustering ON)
        },
      });

      // 1b. Foreground unclustered (icono vehículo)
      this.addLayer({
        id: this.unclusteredForegroundLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']], // Solo features que NO son clusters
        layout: {
          'icon-image': this.getIconExpression(),
          'icon-size': 0.2, // 18px final / 90px natural = 0.2 (vehiculo.png es 90x90)
          'icon-allow-overlap': true,
          'icon-padding': 0,
          'icon-rotation-alignment': 'viewport',
          'icon-pitch-alignment': 'viewport',
          visibility: 'none', // Inicialmente oculto (se muestra cuando clustering ON)
        },
      });
    }

    // 2. BACKGROUND LAYER (brújula rotable o círculo fijo)
    // Usa el source normal (sin clustering)
    this.addLayer({
      id: this.backgroundLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getBackgroundIconExpression(),
        'icon-size': 0.05, // 800 * 0.05 = 40px (circle.png y compass.svg son 800x800)
        'icon-allow-overlap': true,
        'icon-padding': 0,
        'icon-rotation-alignment': 'map', // IMPORTANTE: 'map' para que la brújula apunte al heading correcto
        'icon-rotate': this.getIconRotationExpression(),
        'icon-pitch-alignment': 'viewport', // 'viewport' para que no se pegue al mapa inclinado
      },
    });

    // 3. FOREGROUND LAYER (icono de vehículo)
    // Siempre encima del background
    this.addLayer({
      id: this.foregroundLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getIconExpression(),
        'icon-size': 0.2, // 18px final / 90px natural = 0.2 (vehiculo.png es 90x90)
        'icon-allow-overlap': true,
        'icon-padding': 0,
        'icon-rotation-alignment': 'viewport',
        'icon-pitch-alignment': 'viewport',
      },
    });

    // 4. TEXT LAYER (identificación del vehículo)
    // Muestra el nombre/identificación arriba del ícono
    // IMPORTANTE: Visibilidad controlada por mostrarNombresMarkers
    this.addLayer({
      id: this.textLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'text-field': ['get', 'identificacion'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-anchor': 'bottom',
        'text-offset': [0, -1.8], // Offset hacia arriba para que quede sobre el ícono
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        visibility: this.mostrarNombresMarkers ? 'visible' : 'none', // Controlar visibilidad según configuración
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-width': 2,
      },
    });
  }

  // ========== CLUSTERING ==========

  /**
   * Override de updateClusteringVisibility para manejar el sistema de dos capas
   */
  protected override updateClusteringVisibility(): void {
    if (!this.map || !this.supportsClustering) return;

    if (this.clusteringEnabled) {
      // CLUSTERING ON:
      // - Mostrar clusters (respetando filtro)
      // - Mostrar unclustered (background + foreground, vehículos fuera de clusters, respetando filtro)
      // - Ocultar layers normales (background + foreground + text)
      // IMPORTANTE: Usar this.isVisible en lugar de true para respetar el estado del filtro
      this.setLayerVisibility(
        `${this.entityType}-cluster-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(
        this.unclusteredBackgroundLayerId,
        this.isVisible,
      );
      this.setLayerVisibility(
        this.unclusteredForegroundLayerId,
        this.isVisible,
      );
      this.setLayerVisibility(this.backgroundLayerId, false);
      this.setLayerVisibility(this.foregroundLayerId, false);
      this.setLayerVisibility(this.textLayerId, false); // Siempre oculto cuando clustering ON
    } else {
      // CLUSTERING OFF:
      // - Ocultar clusters
      // - Ocultar unclustered (background + foreground)
      // - Mostrar layers normales (background + foreground, respetando filtro)
      // - Text layer: respetar configuración de mostrarNombresMarkers
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, false);
      this.setLayerVisibility(`${this.entityType}-cluster-count-layer`, false);
      this.setLayerVisibility(this.unclusteredBackgroundLayerId, false);
      this.setLayerVisibility(this.unclusteredForegroundLayerId, false);
      // IMPORTANTE: Usar this.isVisible en lugar de true para respetar el estado del filtro
      this.setLayerVisibility(this.backgroundLayerId, this.isVisible);
      this.setLayerVisibility(this.foregroundLayerId, this.isVisible);
      // Text layer: controlar según mostrarNombresMarkers
      this.updateTextLayerVisibility();
    }
  }

  /**
   * Helper para cambiar visibilidad de un layer
   */
  private setLayerVisibility(layerId: string, visible: boolean): void {
    if (!this.map) return;

    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(
        layerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }

  // ========== VISIBILIDAD GENERAL ==========

  /**
   * Override de setVisibility para manejar el sistema de dos capas
   */
  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;

    // Si clustering está habilitado, manejar visibility de clusters y unclustered
    if (this.clusteringEnabled && this.supportsClustering) {
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, visible);
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        visible,
      );
      this.setLayerVisibility(this.unclusteredBackgroundLayerId, visible);
      this.setLayerVisibility(this.unclusteredForegroundLayerId, visible);
    } else {
      // Si clustering OFF, manejar visibility de layers normales
      this.setLayerVisibility(this.backgroundLayerId, visible);
      this.setLayerVisibility(this.foregroundLayerId, visible);
    }

    // Text layer siempre respeta el estado de mostrarNombresMarkers
    this.updateTextLayerVisibility();
  }

  // ========== OPCIONES ==========

  /**
   * Configura opciones del manager en tiempo de ejecución
   *
   * @param options Objeto con opciones a configurar
   */
  setOptions(options: any): void {
    if (options.mostrarNombresMarkers !== undefined) {
      this.mostrarNombresMarkers = options.mostrarNombresMarkers;
      this.updateTextLayerVisibility();
    }
  }

  /**
   * Actualiza la visibilidad del text layer según la configuración
   */
  private updateTextLayerVisibility(): void {
    if (!this.map) return;

    // Solo mostrar nombres si:
    // 1. mostrarNombresMarkers está en true O hay un vehículo seleccionado
    // 2. El manager está visible (this.isVisible)
    // 3. El clustering está deshabilitado (ya que los nombres no se muestran en clusters)
    const shouldShowText =
      (this.mostrarNombresMarkers || this.idVehiculoSeleccionado !== null) &&
      this.isVisible &&
      !this.clusteringEnabled;

    this.setLayerVisibility(this.textLayerId, shouldShowText);

    // Si hay un vehículo seleccionado, aplicar filtro para mostrar solo su nombre
    if (this.idVehiculoSeleccionado !== null && shouldShowText) {
      this.setTextLayerFilter([
        '==',
        ['get', 'idActivo'],
        this.idVehiculoSeleccionado,
      ]);
    } else if (!this.mostrarNombresMarkers && shouldShowText) {
      // Si no hay mostrarNombresMarkers pero hay seleccionado, no mostrar nada
      this.setLayerVisibility(this.textLayerId, false);
    } else {
      // Si mostrarNombresMarkers está activo, mostrar todos
      this.clearTextLayerFilter();
    }
  }

  /**
   * Aplica un filtro al text layer
   */
  private setTextLayerFilter(filter: mapboxgl.Expression): void {
    if (!this.map || !this.map.getLayer(this.textLayerId)) return;
    this.map.setFilter(this.textLayerId, filter);
  }

  /**
   * Elimina el filtro del text layer
   */
  private clearTextLayerFilter(): void {
    if (!this.map || !this.map.getLayer(this.textLayerId)) return;
    this.map.setFilter(this.textLayerId, null);
  }

  /**
   * Muestra el nombre solo de un vehículo específico
   *
   * @param idVehiculo ID del vehículo (_id del activo, NO del reporte)
   *
   * @example
   * // Mostrar nombre de un vehículo al hacer click
   * vehiculosManager.mostrarNombreDeVehiculo('vehiculo123');
   *
   * // Ocultar nombre (pasar null)
   * vehiculosManager.mostrarNombreDeVehiculo(null);
   */
  public mostrarNombreDeVehiculo(idVehiculo: string | null): void {
    this.idVehiculoSeleccionado = idVehiculo;
    this.updateTextLayerVisibility();
  }
}
